import { AlertTriangle, Check, Info } from 'lucide-react'
import type { ReplaceDesignSourceMode } from '../../api'

export const PUBLISHED_CARD_STATUSES = new Set(['creating', 'created', 'price_set', 'stock_set', 'selling'])

/** Есть ли среди статусов карточек МП уже опубликованные (им нужна отдельная отправка фото). */
export const hasPublishedCardStatus = (cardStatuses: string[]): boolean =>
  cardStatuses.some((status) => PUBLISHED_CARD_STATUSES.has(status))

const modes: Array<{ value: ReplaceDesignSourceMode; title: string; description: string }> = [
  {
    value: 'designs_only',
    title: 'Только макеты',
    description: 'Заменить PDF и превью. Товары и карточки не изменятся.',
  },
  {
    value: 'designs_and_products',
    title: 'Макеты и товары',
    description: 'Дополнительно пересоздать производственные файлы товаров.',
  },
  {
    value: 'full_pipeline',
    title: 'Полный пайплайн',
    description: 'Пересоздать файлы, визуалы и только черновики карточек.',
  },
]

interface ReplaceScenarioFieldsProps {
  mode: ReplaceDesignSourceMode
  onModeChange: (mode: ReplaceDesignSourceMode) => void
  resendPhotos: boolean
  onResendPhotosChange: (value: boolean) => void
  confirmed: boolean
  onConfirmedChange: (value: boolean) => void
  hasPublishedCards: boolean
  confirmText: string
}

/**
 * Шаг «Применение» замены исходника макета: сценарий пересоздания, отправка новых
 * фото в опубликованные карточки и подтверждение. Общий для «Изменить макет»
 * (загрузка PDF) и «Изменить с ИИ».
 */
export function ReplaceScenarioFields({
  mode,
  onModeChange,
  resendPhotos,
  onResendPhotosChange,
  confirmed,
  onConfirmedChange,
  hasPublishedCards,
  confirmText,
}: ReplaceScenarioFieldsProps) {
  return (
    <>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Что пересоздать
        </p>
        <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Сценарий замены макета">
          {modes.map((item) => (
            <button
              key={item.value}
              type="button"
              role="radio"
              aria-checked={mode === item.value}
              aria-pressed={mode === item.value}
              onClick={() => onModeChange(item.value)}
              className={`relative min-h-11 rounded-lg border p-4 pr-9 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                mode === item.value
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
                  : 'border-gray-200 dark:border-zinc-700'
              }`}
            >
              {mode === item.value && (
                <Check className="absolute right-3 top-3 h-5 w-5 text-indigo-600" aria-hidden="true" />
              )}
              <span className="block text-sm font-semibold">{item.title}</span>
              <span className="mt-1 block text-xs text-gray-500 dark:text-zinc-400">{item.description}</span>
            </button>
          ))}
        </div>
      </div>

      {mode === 'full_pipeline' && (
        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 p-3 text-sm dark:border-zinc-700">
          <input
            type="checkbox"
            checked={resendPhotos}
            onChange={(event) => onResendPhotosChange(event.target.checked)}
            className="mt-0.5"
          />
          <span>Отправить новые фото в опубликованные карточки</span>
        </label>
      )}

      {mode === 'full_pipeline' && resendPhotos ? (
        <div className="flex gap-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-200">
          <Info className="mt-0.5 h-5 w-5 shrink-0" />
          <p>
            Когда визуалы пересоздадутся по всем кабинетам, новые фото уйдут во все опубликованные
            карточки: ВБ, ОЗОН, Маркет и сайт. Итог — во вкладке «Отправки» на странице «Изменения карточек
            МП».
          </p>
        </div>
      ) : (
        hasPublishedCards && (
          <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <p>
              Опубликованные карточки сохранят старые фото. Отправить новые можно кнопкой
              «Переотправить фото» на вкладке «Карточки МП».
            </p>
          </div>
        )
      )}

      <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 p-3 text-sm dark:border-zinc-700">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => onConfirmedChange(event.target.checked)}
          className="mt-0.5"
        />
        <span>{confirmText}</span>
      </label>
    </>
  )
}

export default ReplaceScenarioFields
